import { createContext, useContext, useState } from "react";

import { isEqual } from "lodash-es";
import { useSearchParams } from "react-router-dom";

export const FilterAppliedListenerContext = createContext<
  (appliedFilters: URLParamFilter[]) => void
>(() => {});

type URLParamFilterKey = {
  key: string;
  operator?: string;
};

export type URLParamFilter = {
  key: string;
  operator: string;
  values: string[];
};

const parseFilterParam = ([key, value]: [
  string,
  string | undefined,
]): URLParamFilter => {
  const [property, operator = "eq"] = key.slice(7, -1).split("][");
  return { key: property, operator, values: value?.split(",") ?? [] };
};

const paramFilterToKey = (filter: URLParamFilterKey) => {
  return filter.operator
    ? `filter[${filter.key}][${filter.operator}]`
    : `filter[${filter.key}]`;
};

export const useFilter = (
  property: string,
  initialFilters: URLParamFilter[],
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const onFilterApplied = useContext(FilterAppliedListenerContext);
  const [filters, setFilters] = useState<URLParamFilter[]>(() => {
    const values = Array.from(searchParams.entries()).filter(
      ([key]) => isFilterKey(key) && key.startsWith(`filter[${property}]`),
    );

    // If no filters are set in URL, use the default values
    if (values.length !== initialFilters.length) {
      return initialFilters;
    }

    return values.map(parseFilterParam);
  });

  const apply = () => {
    setSearchParams((searchParams) => {
      Array.from(searchParams.keys()).forEach((key) => {
        if (isFilterKey(key) && key.startsWith(`filter[${property}]`)) {
          searchParams.delete(key);
        }
      });

      filters.forEach((filter) => {
        if (filter.values.length > 0) {
          searchParams.set(
            `filter[${filter.key}][${filter.operator}]`,
            filter.values.join(","),
          );
        }
      });

      return searchParams;
    });
    const appliedFilters = filters.filter((filter) => filter.values.length > 0);
    if (appliedFilters.length > 0) {
      onFilterApplied(appliedFilters);
    }
  };

  const reset = (filters: URLParamFilter[] = initialFilters) => {
    setFilters(filters);
    setSearchParams((searchParams) => {
      // Transforming to the array must be done here,
      // otherwise it doesn't work, and only deletes the first filter
      for (const key of Array.from(searchParams.keys())) {
        if (isFilterKey(key) && key.startsWith(`filter[${property}]`)) {
          searchParams.delete(key);
        }
      }

      return searchParams;
    });
  };

  const isFiltering = (keys: URLParamFilterKey[] | URLParamFilterKey) => {
    const filterKeys = Array.isArray(keys)
      ? keys.map((key) => paramFilterToKey(key))
      : [paramFilterToKey(keys)];

    return (
      Array.from(searchParams.entries()).filter(([key]) =>
        filterKeys.some((filterKey) => key.startsWith(filterKey)),
      ).length > 0
    );
  };

  const isApplied = () => {
    const availableFilters = filters.filter(
      (filter) => filter.values.length > 0,
    );
    const filterKeys = availableFilters.map((filter) =>
      paramFilterToKey(filter),
    );
    const urlFilterKeys = Array.from(searchParams.keys()).filter((key) =>
      key.startsWith(`filter[${property}]`),
    );

    return (
      isEqual(filterKeys, urlFilterKeys) &&
      availableFilters.every((filter) => {
        const filterKey = paramFilterToKey(filter);

        return searchParams.has(filterKey, filter.values.join(","));
      })
    );
  };

  return {
    filters,
    apply,
    update: setFilters,
    reset,
    isFiltering,
    updateParams: setSearchParams,
    isApplied,
  } as const;
};

/**
 * Filters getter
 * designed to be used in the main component to get all set filters
 * from the URL, filters are set using individual useFilter hooks
 */
export const useURLParamsFilters = (): Record<string, string> => {
  const [searchParams] = useSearchParams();

  const filters = Array.from(searchParams.entries())
    .filter(([key, value]) => isFilterKey(key) && value.length > 0)
    .map(([key, value]) => [key.includes("][") ? key : `${key}[eq]`, value]);

  return Object.fromEntries(filters);
};

export const useResetURLParamsFilters = () => {
  const [, setSearchParams] = useSearchParams();

  return () => {
    setSearchParams((searchParams) => {
      for (const key of Array.from(searchParams.keys())) {
        if (isFilterKey(key)) {
          searchParams.delete(key);
        }
      }

      return searchParams;
    });
  };
};

/**
 * Utils
 */

export const isFilterKey = (filterKey: string) =>
  filterKey.startsWith("filter[") && filterKey.endsWith("]");
